import {
  useRef,
  useEffect,
  useLayoutEffect,
  useCallback,
  useState,
  useId,
  createContext,
  useContext,
} from 'react'
import { createPortal } from 'react-dom'
import { PopupLayer } from './PopupLayer'
import { aboveLayer, usePopupLayer } from './popupLayers'
import type { CSSProperties, HTMLAttributes, ReactNode, RefObject } from 'react'
import { focusQuietly } from '../utils/quietFocus'

const FOCUSABLE =
  'button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

function focusableItems(root: ParentNode): HTMLElement[] {
  return [...root.querySelectorAll<HTMLElement>(FOCUSABLE)].filter(
    (element) =>
      !element.closest('[hidden], [inert], [aria-hidden="true"]') &&
      getComputedStyle(element).display !== 'none' &&
      getComputedStyle(element).visibility !== 'hidden'
  )
}

interface PopoverProps {
  trigger: ReactNode
  children: ReactNode
  open: boolean
  onOpenChange: (open: boolean) => void
  placement?: 'default' | 'right-start' | 'right'
  align?: 'left' | 'right'
  showCaret?: boolean
  stopTriggerPropagation?: boolean
  verticalAnchorRect?: DOMRect | null
  horizontalAnchorRect?: DOMRect | null
  boundaryRect?: DOMRect | null
  boundaryElementRef?: RefObject<HTMLElement | null>
  anchorElementRef?: RefObject<HTMLElement | null>
  anchorClosestSelector?: string
  /** For right-start placement, center this content item on the anchor row. */
  alignContentItemSelector?: string
  anchorOffsetXpx?: number
  anchorWidthAdjustmentPx?: number
  boundaryPaddingPx?: number
  /** Screen-edge inset; footer controls may need to align below the usual 8px inset. */
  viewportPaddingPx?: number
  contentClassName?: string
  contentStyle?: CSSProperties
  matchTriggerWidth?: boolean
  fitContentWidthSelector?: string
  portalZIndex?: CSSProperties['zIndex']
  wrapperClassName?: string
  triggerClassName?: string
  sideOffsetPx?: number
  triggerMode?: 'click' | 'hover' | 'manual'
  contentId?: string
  contentRole?: 'dialog' | 'tooltip' | 'menu'
  /**
   * A menu moves focus into itself when it opens (keyboard use). False keeps
   * focus where it is, for a menu that opens on its own (an export ready).
   */
  focusOnOpen?: boolean
  contentLabelledBy?: string
  /** The card's own pointer handlers (it's portaled, so its events still reach the trigger's ancestors). */
  contentHandlers?: Pick<
    HTMLAttributes<HTMLDivElement>,
    'onMouseEnter' | 'onMouseLeave' | 'onClick' | 'onDoubleClick'
  >
}

interface PopoverPosition {
  top: number
  left: number
  placedAbove: boolean
  triggerWidth: number
  contentWidth: number
}

const PopoverOwnersContext = createContext<string[]>([])

const VIEWPORT_PADDING = 8
const GAP = 4

function getEstimatedContentWidth(content: HTMLDivElement): number {
  const explicitWidth = content.getBoundingClientRect().width
  if (explicitWidth > 0) return explicitWidth
  return 180
}

function getIntrinsicElementWidth(element: HTMLElement): number {
  const clone = element.cloneNode(true) as HTMLElement
  Object.assign(clone.style, {
    position: 'fixed',
    top: '0',
    left: '-10000px',
    width: 'max-content',
    minWidth: '0',
    maxWidth: 'none',
    visibility: 'hidden',
    pointerEvents: 'none',
  })
  clone.querySelectorAll<HTMLElement>('[data-popover-fit-content-label]').forEach((label) => {
    label.style.flex = '0 0 auto'
    label.style.overflow = 'visible'
    label.style.textOverflow = 'clip'
  })
  document.body.appendChild(clone)
  const width = Math.ceil(clone.getBoundingClientRect().width)
  clone.remove()
  return width
}

function getFittedContentWidth(
  content: HTMLDivElement,
  selector: string,
  minimumWidth: number
): number {
  const contentWidth = content.getBoundingClientRect().width
  let fittedWidth = minimumWidth
  content.querySelectorAll<HTMLElement>(selector).forEach((element) => {
    const intrinsicWidth = getIntrinsicElementWidth(element)
    fittedWidth = Math.max(
      fittedWidth,
      contentWidth - element.getBoundingClientRect().width + intrinsicWidth
    )
  })
  return Math.ceil(fittedWidth)
}

function calculatePosition({
  triggerRect,
  contentWidth,
  contentHeight,
  viewportWidth,
  viewportHeight,
  placement,
  align,
  sideOffsetPx,
  verticalAnchorRect,
  horizontalAnchorRect,
  boundaryRect,
  boundaryPaddingPx,
  viewportPaddingPx,
}: {
  triggerRect: DOMRect
  verticalAnchorRect?: DOMRect | null
  horizontalAnchorRect?: DOMRect | null
  boundaryRect?: DOMRect | null
  boundaryPaddingPx: number
  viewportPaddingPx: number
  contentWidth: number
  contentHeight: number
  viewportWidth: number
  viewportHeight: number
  placement: 'default' | 'right-start' | 'right'
  align: 'left' | 'right'
  sideOffsetPx: number
}): Omit<PopoverPosition, 'triggerWidth' | 'contentWidth'> {
  if (placement === 'right-start' || placement === 'right') {
    const boundaryLeft = boundaryRect ? boundaryRect.left + boundaryPaddingPx : viewportPaddingPx
    const boundaryRight = boundaryRect
      ? boundaryRect.right - boundaryPaddingPx
      : viewportWidth - viewportPaddingPx
    const preferredLeft = triggerRect.right + sideOffsetPx
    const fallbackLeft = triggerRect.left - contentWidth - sideOffsetPx
    const left =
      preferredLeft + contentWidth <= boundaryRight
        ? preferredLeft
        : Math.max(boundaryLeft, fallbackLeft)
    const top =
      placement === 'right'
        ? triggerRect.top + triggerRect.height / 2 - contentHeight / 2
        : (verticalAnchorRect?.top ?? triggerRect.top)

    const boundaryTop = boundaryRect ? boundaryRect.top + boundaryPaddingPx : viewportPaddingPx
    const boundaryBottom = boundaryRect
      ? boundaryRect.bottom - boundaryPaddingPx
      : viewportHeight - viewportPaddingPx

    return {
      top: Math.max(boundaryTop, Math.min(top, boundaryBottom - contentHeight)),
      left: Math.max(boundaryLeft, Math.min(left, boundaryRight - contentWidth)),
      placedAbove: false,
    }
  }

  const verticalRect = verticalAnchorRect ?? triggerRect
  const horizontalRect = horizontalAnchorRect ?? triggerRect
  const verticalGap = verticalAnchorRect ? -1 : sideOffsetPx
  const spaceBelow = viewportHeight - verticalRect.bottom
  const spaceAbove = verticalRect.top
  const fitsBelow = spaceBelow >= contentHeight + GAP + viewportPaddingPx
  const fitsAbove = spaceAbove >= contentHeight + GAP + viewportPaddingPx
  const placedAbove = !fitsBelow && fitsAbove
  let top = placedAbove
    ? verticalRect.top - contentHeight - verticalGap
    : verticalRect.bottom + verticalGap
  top = Math.max(boundaryRect?.top ?? viewportPaddingPx, top)

  const boundaryLeft = boundaryRect ? boundaryRect.left + boundaryPaddingPx : viewportPaddingPx
  const boundaryRight = boundaryRect
    ? boundaryRect.right - boundaryPaddingPx
    : viewportWidth - viewportPaddingPx
  let left = align === 'right' ? horizontalRect.right - contentWidth : horizontalRect.left
  if (align === 'left' && left + contentWidth > boundaryRight) {
    left = horizontalRect.right - contentWidth
  }
  if (left < boundaryLeft) {
    left = horizontalRect.left
  }
  left = Math.max(boundaryLeft, Math.min(left, boundaryRight - contentWidth))

  return { top, left, placedAbove }
}

export default function Popover({
  trigger,
  children,
  open,
  onOpenChange,
  placement = 'default',
  align = 'left',
  showCaret = true,
  stopTriggerPropagation = false,
  verticalAnchorRect = null,
  horizontalAnchorRect = null,
  boundaryRect = null,
  boundaryElementRef,
  anchorElementRef,
  anchorClosestSelector,
  alignContentItemSelector,
  anchorOffsetXpx = 0,
  anchorWidthAdjustmentPx = 0,
  boundaryPaddingPx = 0,
  viewportPaddingPx = VIEWPORT_PADDING,
  contentClassName,
  contentStyle,
  matchTriggerWidth = false,
  fitContentWidthSelector,
  portalZIndex,
  wrapperClassName,
  triggerClassName,
  sideOffsetPx = GAP,
  triggerMode = 'click',
  contentId,
  contentRole = 'dialog',
  focusOnOpen = true,
  contentLabelledBy,
  contentHandlers,
}: PopoverProps) {
  const popoverId = useId()
  const parentOwners = useContext(PopoverOwnersContext)
  const inheritedLayer = usePopupLayer()
  const popupLayer = portalZIndex ?? inheritedLayer
  const triggerRef = useRef<HTMLDivElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)
  const animationFrameRef = useRef<number | null>(null)
  const keyboardOpenRef = useRef(false)
  const openAtEndRef = useRef(false)
  const returnFocusRef = useRef<HTMLElement | null>(null)

  useLayoutEffect(() => {
    if (
      !open ||
      !focusOnOpen ||
      (contentRole !== 'menu' &&
        (contentRole !== 'dialog' || triggerMode !== 'click' || !keyboardOpenRef.current))
    )
      return
    keyboardOpenRef.current = false
    const items = contentRef.current ? focusableItems(contentRef.current) : []
    const first = openAtEndRef.current ? items[items.length - 1] : items[0]
    openAtEndRef.current = false
    if (first) focusQuietly(first, { preventScroll: true })
  }, [contentRole, focusOnOpen, open, triggerMode])
  const [position, setPosition] = useState<PopoverPosition>({
    top: 0,
    left: 0,
    placedAbove: false,
    triggerWidth: 0,
    contentWidth: 0,
  })

  const getAnchorElement = useCallback(() => {
    if (anchorElementRef?.current) return anchorElementRef.current
    if (anchorClosestSelector) {
      return triggerRef.current?.closest<HTMLElement>(anchorClosestSelector) ?? triggerRef.current
    }
    return triggerRef.current
  }, [anchorClosestSelector, anchorElementRef])

  const getAnchorRect = useCallback(() => {
    const rect = getAnchorElement()?.getBoundingClientRect()
    if (!rect || (anchorOffsetXpx === 0 && anchorWidthAdjustmentPx === 0)) return rect
    return new DOMRect(
      rect.x + anchorOffsetXpx,
      rect.y,
      Math.max(0, rect.width + anchorWidthAdjustmentPx),
      rect.height
    )
  }, [anchorOffsetXpx, anchorWidthAdjustmentPx, getAnchorElement])

  const handleClickOutside = useCallback(
    (e: MouseEvent) => {
      const target = e.target as Node
      // Check if click is outside both trigger and content (content is in portal)
      const clickedTrigger = triggerRef.current?.contains(target)
      const descendantPortal = (target as Element).closest?.('[data-popover-owners]')
      const clickedDescendant = descendantPortal
        ?.getAttribute('data-popover-owners')
        ?.split(' ')
        .includes(popoverId)
      const clickedContent = contentRef.current?.contains(target) || clickedDescendant
      if (!clickedTrigger && !clickedContent) {
        onOpenChange(false)
      }
    },
    [onOpenChange, popoverId]
  )

  // An open popover owns Escape: it closes, and marks the key handled so the
  // panel or dialog around it (listening on the document too) stays open. One
  // a control inside already used (closing its own list) leaves it open.
  const handleEscape = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !e.defaultPrevented) {
        const hasChild = [...document.querySelectorAll('[data-popover-owners]')].some(
          (element) =>
            element !== contentRef.current &&
            element.getAttribute('data-popover-owners')?.split(' ').includes(popoverId)
        )
        if (hasChild) return
        e.preventDefault()
        if (
          contentRef.current?.contains(document.activeElement) &&
          returnFocusRef.current?.isConnected
        ) {
          focusQuietly(returnFocusRef.current, { preventScroll: true })
        }
        onOpenChange(false)
      }
    },
    [onOpenChange, popoverId]
  )

  // A table grid's controls keep their presses from the grid (a row's
  // checkbox doesn't start a cell selection), so the document never hears
  // them, and a popover over the grid (the Export menu) stayed open, stale.
  // A press inside `[data-closes-popovers]` (the grid's frame) is heard as it
  // starts instead. Elsewhere a stopped press still keeps a popover open, as
  // portaled dropdowns rely on.
  const handleGridPress = useCallback(
    (e: PointerEvent) => {
      if ((e.target as Element).closest?.('[data-closes-popovers]')) handleClickOutside(e)
    },
    [handleClickOutside]
  )

  useEffect(() => {
    if (open) {
      document.addEventListener('mousedown', handleClickOutside)
      document.addEventListener('pointerdown', handleGridPress, true)
      document.addEventListener('keydown', handleEscape)
      return () => {
        document.removeEventListener('mousedown', handleClickOutside)
        document.removeEventListener('pointerdown', handleGridPress, true)
        document.removeEventListener('keydown', handleEscape)
      }
    }
  }, [open, handleClickOutside, handleGridPress, handleEscape])

  useEffect(() => {
    if (!open || (contentRole !== 'menu' && (contentRole !== 'dialog' || triggerMode !== 'click')))
      return
    const handleKeys = (event: KeyboardEvent) => {
      const content = contentRef.current
      const active = document.activeElement
      if (event.defaultPrevented || !content || !active || !content.contains(active)) return
      const items = focusableItems(content)
      const index = items.indexOf(active as HTMLElement)
      if (
        event.key === 'Tab' &&
        (contentRole === 'menu' ||
          (event.shiftKey && index === 0) ||
          (!event.shiftKey && index === items.length - 1))
      ) {
        const trigger = returnFocusRef.current
        if (!trigger?.isConnected) return
        event.preventDefault()
        onOpenChange(false)
        const pageItems = focusableItems(document).filter((item) => !content.contains(item))
        const destination = event.shiftKey
          ? trigger
          : (pageItems[pageItems.indexOf(trigger) + 1] ?? trigger)
        focusQuietly(destination, { preventScroll: true })
      } else if (
        active instanceof HTMLButtonElement &&
        ['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)
      ) {
        event.preventDefault()
        const next =
          event.key === 'Home'
            ? 0
            : event.key === 'End'
              ? items.length - 1
              : (index + (event.key === 'ArrowDown' ? 1 : -1) + items.length) % items.length
        items[next]?.focus()
      }
    }
    document.addEventListener('keydown', handleKeys)
    return () => document.removeEventListener('keydown', handleKeys)
  }, [contentRole, onOpenChange, open, triggerMode])

  const updatePosition = useCallback(() => {
    if (!open || !triggerRef.current || !contentRef.current) return

    const triggerRect = getAnchorRect()
    if (!triggerRect) return
    const contentRect = contentRef.current.getBoundingClientRect()
    const alignedItem =
      placement === 'right-start' && alignContentItemSelector
        ? contentRef.current.querySelector<HTMLElement>(alignContentItemSelector)
        : null
    const itemRect = alignedItem?.getBoundingClientRect()
    const alignedVerticalAnchor = itemRect
      ? new DOMRect(
          triggerRect.x,
          triggerRect.top +
            triggerRect.height / 2 -
            (itemRect.top - contentRect.top + itemRect.height / 2),
          triggerRect.width,
          triggerRect.height
        )
      : verticalAnchorRect
    const viewportWidth = window.innerWidth
    const viewportHeight = window.innerHeight
    const resolvedBoundaryRect =
      boundaryElementRef?.current?.getBoundingClientRect() ?? boundaryRect
    const minimumWidth = matchTriggerWidth ? triggerRect.width : 0
    const measuredContentWidth = fitContentWidthSelector
      ? getFittedContentWidth(contentRef.current, fitContentWidthSelector, minimumWidth)
      : Math.max(getEstimatedContentWidth(contentRef.current), minimumWidth)
    const availableContentWidth = resolvedBoundaryRect
      ? resolvedBoundaryRect.width - boundaryPaddingPx * 2
      : viewportWidth - viewportPaddingPx * 2
    const contentWidth = Math.min(measuredContentWidth, Math.max(0, availableContentWidth))

    setPosition({
      ...calculatePosition({
        triggerRect,
        verticalAnchorRect: alignedVerticalAnchor,
        horizontalAnchorRect,
        boundaryRect: resolvedBoundaryRect,
        boundaryPaddingPx,
        viewportPaddingPx,
        contentWidth,
        contentHeight: contentRect.height,
        viewportWidth,
        viewportHeight,
        placement,
        align,
        sideOffsetPx,
      }),
      triggerWidth: triggerRect.width,
      contentWidth,
    })
  }, [
    align,
    alignContentItemSelector,
    boundaryElementRef,
    boundaryPaddingPx,
    viewportPaddingPx,
    boundaryRect,
    horizontalAnchorRect,
    getAnchorRect,
    fitContentWidthSelector,
    matchTriggerWidth,
    open,
    placement,
    sideOffsetPx,
    verticalAnchorRect,
  ])

  // Measure and position the popover after it renders.
  useLayoutEffect(() => {
    updatePosition()
  }, [updatePosition])

  // Keep portaled popovers anchored when surrounding layout shifts, such as
  // sidebar collapse/expand transitions moving the trigger after open.
  useEffect(() => {
    if (!open) return

    let previousTriggerRect: DOMRect | null = null
    let previousContentRect: DOMRect | null = null

    const hasRectChanged = (previous: DOMRect | null, next: DOMRect) =>
      !previous ||
      previous.top !== next.top ||
      previous.left !== next.left ||
      previous.width !== next.width ||
      previous.height !== next.height

    const tick = () => {
      const triggerRect = getAnchorRect()
      const contentRect = contentRef.current?.getBoundingClientRect()

      if (
        triggerRect &&
        contentRect &&
        (hasRectChanged(previousTriggerRect, triggerRect) ||
          hasRectChanged(previousContentRect, contentRect))
      ) {
        previousTriggerRect = triggerRect
        previousContentRect = contentRect
        updatePosition()
      }

      animationFrameRef.current = window.requestAnimationFrame(tick)
    }

    const resizeObserver =
      typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(updatePosition)
    const anchorElement = getAnchorElement()
    if (anchorElement) resizeObserver?.observe(anchorElement)
    if (triggerRef.current) resizeObserver?.observe(triggerRef.current)
    if (contentRef.current) resizeObserver?.observe(contentRef.current)
    const mutationObserver =
      fitContentWidthSelector && typeof MutationObserver !== 'undefined'
        ? new MutationObserver(updatePosition)
        : null
    if (contentRef.current) {
      mutationObserver?.observe(contentRef.current, {
        childList: true,
        subtree: true,
        characterData: true,
      })
    }

    window.addEventListener('resize', updatePosition)
    window.addEventListener('scroll', updatePosition, true)
    animationFrameRef.current = window.requestAnimationFrame(tick)

    return () => {
      window.removeEventListener('resize', updatePosition)
      window.removeEventListener('scroll', updatePosition, true)
      resizeObserver?.disconnect()
      mutationObserver?.disconnect()
      if (animationFrameRef.current !== null) {
        window.cancelAnimationFrame(animationFrameRef.current)
        animationFrameRef.current = null
      }
    }
  }, [fitContentWidthSelector, getAnchorElement, getAnchorRect, open, updatePosition])

  return (
    <div className={wrapperClassName ?? 'inline-block'}>
      <div
        ref={triggerRef}
        className={triggerClassName}
        onMouseEnter={() => {
          if (triggerMode === 'hover') onOpenChange(true)
        }}
        onMouseLeave={() => {
          if (triggerMode === 'hover') onOpenChange(false)
        }}
        onFocus={() => {
          if (triggerMode === 'hover') onOpenChange(true)
        }}
        onBlur={() => {
          if (triggerMode === 'hover') onOpenChange(false)
        }}
        onPointerDown={(event) => {
          keyboardOpenRef.current = false
          if (stopTriggerPropagation) event.stopPropagation()
        }}
        onKeyDown={(event) => {
          if (
            triggerMode !== 'click' ||
            (contentRole !== 'dialog' && contentRole !== 'menu') ||
            event.defaultPrevented
          )
            return
          if (
            open &&
            contentRef.current &&
            (event.key === 'ArrowDown' ||
              event.key === 'ArrowUp' ||
              (event.key === 'Tab' && !event.shiftKey))
          ) {
            const items = focusableItems(contentRef.current)
            const item = event.key === 'ArrowUp' ? items[items.length - 1] : items[0]
            if (item) {
              event.preventDefault()
              item.focus()
            }
            return
          }
          if (event.key === 'Enter' || event.key === ' ') keyboardOpenRef.current = true
          if (!open && (event.key === 'ArrowDown' || event.key === 'ArrowUp')) {
            event.preventDefault()
            keyboardOpenRef.current = true
            openAtEndRef.current = event.key === 'ArrowUp'
            returnFocusRef.current = event.target as HTMLElement
            onOpenChange(true)
          }
        }}
        onClick={(event) => {
          if (stopTriggerPropagation) event.stopPropagation()
          if (triggerMode !== 'click') return
          if (!open) {
            returnFocusRef.current =
              event.target instanceof Element
                ? event.target.closest<HTMLElement>('button, a[href], [tabindex]')
                : null
          }
          onOpenChange(!open)
        }}
      >
        {trigger}
      </div>
      {open &&
        createPortal(
          <div
            id={contentId}
            ref={contentRef}
            role={contentRole}
            aria-labelledby={contentLabelledBy}
            aria-modal={contentRole === 'dialog' ? 'false' : undefined}
            data-placement={position.placedAbove ? 'above' : 'below'}
            data-portal
            data-popover-owners={[...parentOwners, popoverId].join(' ')}
            // Containers that heard Escape first (their listener is older)
            // look for this and leave the key to the popover.
            data-escape-consumer="popover"
            {...contentHandlers}
            className={`fixed rounded-lg border border-default surface-default shadow-lg ${
              contentClassName ?? 'p-3'
            }`}
            style={{
              top: position.top,
              left: position.left,
              zIndex: popupLayer,
              minWidth: matchTriggerWidth ? position.triggerWidth : undefined,
              width:
                fitContentWidthSelector && position.contentWidth > 0
                  ? position.contentWidth
                  : undefined,
              ...contentStyle,
            }}
          >
            {/* Caret arrow pointing to the trigger */}
            {showCaret && (
              <div
                className={`absolute ${
                  position.placedAbove
                    ? 'top-full left-3 -mt-[1px] border-l-[6px] border-r-[6px] border-t-[6px] border-l-transparent border-r-transparent border-t-[color:var(--surface-default)] drop-shadow-[0_1px_1px_rgba(0,0,0,0.1)]'
                    : 'bottom-full left-3 -mb-[1px] border-b-[6px] border-l-[6px] border-r-[6px] border-b-[color:var(--surface-default)] border-l-transparent border-r-transparent drop-shadow-[0_-1px_1px_rgba(0,0,0,0.1)]'
                }`}
                style={{ width: 0, height: 0 }}
              />
            )}
            <PopoverOwnersContext.Provider value={[...parentOwners, popoverId]}>
              <PopupLayer layer={aboveLayer(popupLayer)}>{children}</PopupLayer>
            </PopoverOwnersContext.Provider>
          </div>,
          document.body
        )}
    </div>
  )
}
